export type MusicDNAKey =
  | "감성파"
  | "리듬메이커"
  | "섬세파"
  | "내면파"
  | "에너지파"
  | "감각파"
  | "콘셉트파"
  | "즉흥파";

export interface MusicDNAMetrics {
  tempo?: number | null;
  energyLevel?: number | null;
  dynamicRange?: number | null;
  spectralCentroid?: number | null;
  emotion?: string | null;
  genre?: string | null;
}

export interface MusicDNAResult {
  key: MusicDNAKey;
  label: string;
  emoji: string;
  title: string;
  description: string;
  tagline: string;
  metrics?: MusicDNAMetrics;
}

export interface AnalyzeMusicDNAInput {
  tempo?: number | null;
  energyLevel?: number | null;
  dynamicRange?: number | null;
  spectralCentroid?: number | null;
  emotion?: string | null;
  genre?: string | null;
}

const MUSIC_DNA_DETAILS: Record<MusicDNAKey, Omit<MusicDNAResult, "key" | "metrics">> = {
  감성파: {
    label: "감성파",
    emoji: "💧",
    title: "감성파 💧",
    description:
      "섬세한 멜로디와 잔잔한 템포 속에서 감정을 촘촘히 쌓아 올리는 타입이에요. 음악이 끝난 뒤에도 마음속에 여운을 남기는 이야기꾼이 바로 감성파예요.",
    tagline: "따뜻한 공감을 나누는 감정 스토리텔러",
  },
  리듬메이커: {
    label: "리듬메이커",
    emoji: "🥁",
    title: "리듬메이커 🥁",
    description:
      "몸이 먼저 반응할 만큼 강한 비트와 그루브를 중심으로 음악을 완성합니다. 반복되는 리듬 속에서도 새로운 변주를 찾는 본능적인 리듬 메이커예요.",
    tagline: "비트 한 박자로 분위기를 뒤집는 리듬 리더",
  },
  섬세파: {
    label: "섬세파",
    emoji: "🎹",
    title: "섬세파 🎹",
    description:
      "사운드의 결 하나까지 놓치지 않는 완벽주의자로, 음향의 균형과 감정의 디테일을 치밀하게 다듬습니다. 작은 울림에도 의미를 담아내는 정교함이 장점이에요.",
    tagline: "미세한 뉘앙스로 감동을 완성하는 디테일 장인",
  },
  내면파: {
    label: "내면파",
    emoji: "🌙",
    title: "내면파 🌙",
    description:
      "차분한 템포와 낮은 에너지 안에서 깊은 이야기를 전합니다. 소리 사이의 여백을 활용해 듣는 사람의 감정을 조용히 끌어올리는 스토리텔러예요.",
    tagline: "잔잔한 순간에 깊이를 더하는 감성 잠수함",
  },
  에너지파: {
    label: "에너지파",
    emoji: "⚡️",
    title: "에너지파 ⚡️",
    description:
      "빠른 템포와 밝은 톤을 기반으로 폭발적인 에너지를 뿜어내는 타입입니다. 노래 한 곡만으로 공간의 온도를 끌어올리고, 청자를 순식간에 몰입시키는 퍼포머예요.",
    tagline: "무대 위를 밝히는 하이볼트 퍼포머",
  },
  감각파: {
    label: "감각파",
    emoji: "🌈",
    title: "감각파 🌈",
    description:
      "화려한 톤과 기발한 사운드 디테일로 청각적인 색감을 그려냅니다. 새로운 레이어를 겹겹이 쌓아 감각적인 공간을 만드는 아티스트예요.",
    tagline: "소리로 풍경을 그리는 컬러리스트",
  },
  콘셉트파: {
    label: "콘셉트파",
    emoji: "🧩",
    title: "콘셉트파 🧩",
    description:
      "스토리와 아이디어를 중심으로 음악을 설계하는 창작자입니다. 장르의 규칙을 뒤섞고 새로운 세계관을 펼치는 실험적인 프로듀서형이에요.",
    tagline: "아이디어로 음악을 재조립하는 크리에이터",
  },
  즉흥파: {
    label: "즉흥파",
    emoji: "🔥",
    title: "즉흥파 🔥",
    description:
      "순간의 감정과 영감을 그대로 표출하며 음악을 만들어 갑니다. 규칙보다는 흐름을 따르고, 즉흥적인 표현으로 강한 여운을 남기는 타입이에요.",
    tagline: "감정의 불꽃을 즉석에서 터뜨리는 프리 스타일러",
  },
};

function buildResult(
  key: MusicDNAKey,
  metrics?: MusicDNAMetrics
): MusicDNAResult {
  const detail = MUSIC_DNA_DETAILS[key];
  return {
    key,
    ...detail,
    metrics,
  };
}

function normalizeMetric(value?: number | null): number | null {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return null;
  }
  return value;
}

export function analyzeMusicDNA(
  input: AnalyzeMusicDNAInput
): MusicDNAResult {
  const tempo = normalizeMetric(input.tempo) ?? 0;
  const energyLevel = normalizeMetric(input.energyLevel) ?? 0;
  const dynamicRange = normalizeMetric(input.dynamicRange) ?? 0;
  const spectralCentroid = normalizeMetric(input.spectralCentroid) ?? 0;
  const emotion = input.emotion?.toLowerCase() ?? "";
  const genre = input.genre?.toLowerCase() ?? "";

  const metrics: MusicDNAMetrics = {
    tempo,
    energyLevel,
    dynamicRange,
    spectralCentroid,
    emotion: emotion || undefined,
    genre: genre || undefined,
  };

  const isLowEnergy = energyLevel <= 0.35;
  const isVeryLowEnergy = energyLevel <= 0.22;
  const isHighEnergy = energyLevel >= 0.6;
  const isMediumEnergy = energyLevel >= 0.45 && energyLevel < 0.6;
  const isBrightTone = spectralCentroid >= 0.62;
  const isSoftTone = spectralCentroid <= 0.45;
  const isExpressiveDynamics = dynamicRange >= 0.55;
  const isSubtleDynamics = dynamicRange <= 0.4;

  if (genre === "experimental") {
    return buildResult("콘셉트파", metrics);
  }

  if (emotion === "chaotic" || emotion === "wild" || emotion === "free") {
    return buildResult("즉흥파", metrics);
  }

  if (isVeryLowEnergy && tempo <= 95) {
    return buildResult("내면파", metrics);
  }

  if (isLowEnergy && isSoftTone && tempo <= 115) {
    return buildResult("감성파", metrics);
  }

  if (isHighEnergy && tempo >= 130 && isBrightTone) {
    return buildResult("에너지파", metrics);
  }

  if ((isHighEnergy || isMediumEnergy) && tempo >= 100 && tempo <= 130) {
    return buildResult("리듬메이커", metrics);
  }

  if (isBrightTone && isSubtleDynamics) {
    return buildResult("감각파", metrics);
  }

  if (isSoftTone && isExpressiveDynamics) {
    return buildResult("감성파", metrics);
  }

  return buildResult("섬세파", metrics);
}

export const MUSIC_DNA = MUSIC_DNA_DETAILS;
